import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { SelfRecorder } from './self-recorder';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { Mic, Volume2 } from 'lucide-react';
import { AudioSettingsPopover, useAudioSettings } from '../../../custom/audio-settings';
import { WorkbookPanel, ZineButton } from '../../zine';
import { zineColors } from '../../zine/zine-theme';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  WorkbookVerdict,
  workbookCardStyle,
} from './zine-workbook';

interface RecordedExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

export function RecordedExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: RecordedExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const initialRecordingUrl = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Recorded) {
      return submission.recordingUrl ?? '';
    }
    return '';
  }, [content.lastSubmission]);

  const [recordingUrl, setRecordingUrl] = useState(initialRecordingUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Audio device selection
  const { selectedMicId, setSelectedMicId, getMicConstraints } = useAudioSettings();

  useEffect(() => {
    setRecordingUrl(initialRecordingUrl);
  }, [content.id, initialRecordingUrl]);

  const handleRecordingUploaded = useCallback((url: string) => {
    setRecordingUrl(url);
  }, []);

  const handleRecordingCleared = useCallback(() => {
    setRecordingUrl('');
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.Recorded,
        recordingUrl,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.record')}
        title={
          content.title ? renderInlineMarkdown(content.title, 'recorded-exercise-title') : undefined
        }
        hint={t('zine.workbook.recordPrompt')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!recordingUrl || isSubmitting}>
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.submitRecording')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        {!isReadOnly && (
          <div className="mb-2 flex justify-end">
            <AudioSettingsPopover
              selectedMicId={selectedMicId}
              onMicSelect={setSelectedMicId}
              showSpeakerSelection={false}
            />
          </div>
        )}

        <ContentDescription text={content.description} markdown className="mb-3" />

        {isReadOnly ? (
          <div className="mb-4 p-4" style={workbookCardStyle}>
            {recordingUrl ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full"
                    style={{ background: zineColors.redApplied }}
                  >
                    <Volume2 className="h-5 w-5 text-white" />
                  </div>
                  <p className="m-0 text-sm font-semibold" style={{ color: zineColors.ink }}>
                    {t('zine.workbook.recordedAnswer')}
                  </p>
                </div>
                <audio
                  controls
                  src={recordingUrl}
                  className="w-full rounded-lg"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div
                  className="mb-3 flex h-16 w-16 items-center justify-center rounded-full"
                  style={{ background: zineColors.baby }}
                >
                  <Mic className="h-8 w-8" style={{ color: zineColors.charm }} />
                </div>
                <p className="m-0 text-sm" style={{ color: zineColors.warmGray }}>
                  {t('zine.workbook.noRecording')}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4 p-4" style={workbookCardStyle}>
            <SelfRecorder
              value={recordingUrl || null}
              maxDurationSec={content.maxDurationSec ?? 60}
              onRecordingUploaded={handleRecordingUploaded}
              onRecordingCleared={handleRecordingCleared}
              getMicConstraints={getMicConstraints}
            />
          </div>
        )}
      </WorkbookPanel>
    </form>
  );
}
